import type { ReactNode } from "react";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { useFocusedTasksClientId } from "@components/organizationView/clients-tasks/focusedTasksClient";
import { getOrganizationClientTasksLink } from "@components/organizationView/clients-tasks/organizationTaskLink";
import { MONTH_END_CLOSE_VIEW_ID } from "@components/tasks/views/OrganizationTaskViews";
import type { SideNavTarget } from "@design-system/SideNav/SideNav.types";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useScope } from "@state/scopeContext.lib";
import { ClientRowMenu } from "./ClientRowMenu";

type ClientRowTarget = SideNavTarget & {
    isActive?: boolean;
    hoverAction: ReactNode;
};

/**
 * Whether a client's row shows as the current page: with close management on,
 * while the org Actions page is focused on that client. The Actions row hands
 * its highlight to a shown client row by the same rule.
 */
export const useIsClientRowActive = () => {
    const { closeManagement: isCloseManagementEnabled } = useTypedFlags();
    const focusedClientId = useFocusedTasksClientId();
    return (clientId: string) =>
        isCloseManagementEnabled && focusedClientId === clientId;
};

/**
 * Where a recent client's row in the org side nav goes: with close
 * management on, the org Actions page's Month-end close view filtered to the
 * client (active while
 * the page is focused on it); otherwise it switches into the client. Either
 * way its hover action is the client's `⋯` menu.
 */
export const useClientRowTarget = (workspace: {
    id: string;
    name: string;
}): ClientRowTarget => {
    const { closeManagement: isCloseManagementEnabled } = useTypedFlags();
    const { changeActiveWorkspace } = useScope();
    const isClientRowActive = useIsClientRowActive();
    const openClient = () =>
        changeActiveWorkspace(
            workspace.id,
            OrganizationMemberClientAccessSource.SWITCHER,
        );
    const hoverAction = (
        <ClientRowMenu client={workspace} onOpenClient={openClient} />
    );

    if (!isCloseManagementEnabled) {
        return { onClick: openClient, hoverAction };
    }

    return {
        to: getOrganizationClientTasksLink({
            workspaceId: workspace.id,
            viewId: MONTH_END_CLOSE_VIEW_ID,
        }),
        isActive: isClientRowActive(workspace.id),
        hoverAction,
    };
};
